import { MeQuery } from "generated/graphql";
import { Account, Vendor } from "pages/userAuthentication/signUp";
import { DashboardProfileData } from ".";

type MeData = NonNullable<MeQuery["me"]>;

export const emptyDashboardProfileData: DashboardProfileData = {
  account: {
    email: "",
    password: "",
    googleAccessToken: null,
    authProvider: null,
    firstName: "",
    lastName: "",
    userType: "user",
    phoneNumber: "",
    address: "",
    latitude: null,
    longitude: null,
    placeId: null,
    profilePhotoUrl: null,
    profilePhoto: null,
    jobTitle: null,
    onboardingCompleted: false,
    vendorId: null,
    accessLevel: null,
    emailVerified: false,
  },
  vendor: null,
};

export function loadAccount(meData: MeData): Account {
  return {
    email: meData.email || "",
    password: "",
    googleAccessToken: null,
    authProvider: meData.authProvider || null,
    firstName: meData.firstName || "",
    lastName: meData.lastName || "",
    userType: (meData.userType as "user" | "vendor") || "user",
    phoneNumber: meData.phoneNumber || "",
    address: meData.address || "",
    latitude: meData.latitude ?? null,
    longitude: meData.longitude ?? null,
    placeId: meData.placeId ?? null,
    profilePhotoUrl: meData.profilePhotoUrl || null,
    profilePhoto: null,
    jobTitle: meData.jobTitle || null,
    onboardingCompleted: meData.onboardingCompleted || false,
    vendorId: meData.vendor?.id || null,
    accessLevel: meData.accessLevel || null,
    emailVerified: true,
  };
}

export function loadVendor(meData: MeData): Vendor | null {
  if (!meData.vendor) return null;
  const v = meData.vendor;
  return {
    businessName: v.businessName,
    operatingAs: v.operatingAs || null,
    operatingAsSameAsLegal: !v.operatingAs || v.operatingAs === v.businessName,
    mailingSameAsBusiness: v.mailingSameAsBusiness,
    mailingAddress: v.mailingAddress ?? null,
    businessWebsite: v.businessWebsite || null,
    organizationType: v.organizationType,
    primaryContactFirstName: v.primaryContactFirstName,
    primaryContactLastName: v.primaryContactLastName,
    primaryContactPosition: v.primaryContactPosition,
    primaryContactEmail: v.primaryContactEmail,
    primaryContactPhone: v.primaryContactPhone,
    notificationEmail: v.notificationEmail ?? null,
    primaryContactIsSignatory: v.primaryContactIsSignatory,
    signatoryFirstName: v.signatoryFirstName ?? null,
    signatoryLastName: v.signatoryLastName ?? null,
    signatoryPosition: v.signatoryPosition ?? null,
    signatoryEmail: v.signatoryEmail ?? null,
    signatoryPhone: v.signatoryPhone ?? null,
    businessNumber: v.businessNumber,
    proofOfInsurance: null,
    proofOfInsuranceURL: v.proofOfInsuranceURL,
    insuranceExpiryDate: v.insuranceExpiryDate ?? "",
    businessLicense: null,
    businessLicenseURL: v.businessLicenseURL,
    offeringEvInstallation: v.offeringEvInstallation,
    province: v.province,
    electricalLicense: null,
    electricalLicenseURL: v.electricalLicenseURL ?? null,
    electricalLicenseNumber: v.electricalLicenseNumber ?? null,
    electricalLicenseExpiryDate: v.electricalLicenseExpiryDate ?? "",
    declarationAccepted: Boolean(v.termsAcceptedAt),
    termsAccepted: Boolean(v.termsAcceptedAt),
    brandLogo: v.brandLogoURL || null,
    brandLogoUrl: v.brandLogoURL || null,
    brandAbout: v.brandAbout || null,
    brandGoogleReview: v.brandGoogleReview || null,
    publicEmail: v.publicEmail ?? null,
    publicPhone: v.publicPhone ?? null,
  };
}

export function loadProfileData(meData: MeData): DashboardProfileData {
  return {
    account: loadAccount(meData),
    vendor: loadVendor(meData),
  };
}
